import type { Context } from "../context.js";
import type { EmitFn } from "../adapters.js";

/**
 * Event delegation: un único listener en document captura TODOS los clics, sin
 * conocer de antemano los botones del publisher. Deriva click / cta_click /
 * outbound_click / rage_click y adaptation_click (si el clic cae en contenido
 * inyectado por una regla).
 */

function buildSelector(el: Element): string {
  if (el.id) return `#${el.id}`;
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute("class") || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return cls.length ? `${tag}.${cls.join(".")}` : tag;
}

export function startClicks(ctx: Context, emit: EmitFn) {
  let lastTarget: Element | null = null;
  let repeatCount = 0;
  let lastTime = 0;

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      // adaptation_click: ¿cayó dentro de algo que inyectamos? Cerrar el popup
      // (data-tw-dismiss) NO es un clic de contenido: ya emite adaptation_dismiss, así que
      // no lo contamos. `cta` marca si el clic fue sobre algo realmente clicable
      // (enlace/botón/CTA): un test de clic solo convierte con cta=true — clicar el fondo
      // del overlay o un hueco del banner no es una conversión.
      const injected = target.closest<HTMLElement>("[data-tw-injected]");
      if (injected && !target.closest("[data-tw-dismiss]")) {
        // `cta`: el clic cayó en un elemento clicable DENTRO de lo inyectado. Acotamos con
        // injected.contains para no marcar cta=true por un <a> ANCESTRO del publisher que
        // envuelva nuestro contenido (clicar un hueco contaría como conversión si no).
        const clickable = target.closest<HTMLElement>(
          "a, button, [role='button'], [data-tw-cta], [data-tw-goal]"
        );
        const cta = !!clickable && injected.contains(clickable);
        emit("adaptation_click", {
          ruleId: injected.getAttribute("data-tw-rule") || "",
          variantKey: injected.getAttribute("data-tw-variant") || "",
          cta,
        });
      }

      const actionable = target.closest<HTMLElement>("a, button, [role='button']");
      if (!actionable) return;

      const selector = buildSelector(actionable);
      ctx.state.clicks[selector] = (ctx.state.clicks[selector] || 0) + 1;

      const text = (actionable.innerText || "").trim().slice(0, 80);
      const href = actionable.getAttribute("href") || undefined;

      // rage_click: >=3 clics rápidos en el mismo elemento.
      const now = Date.now();
      if (actionable === lastTarget && now - lastTime < 500) {
        repeatCount++;
        if (repeatCount >= 3) emit("rage_click", { selector });
      } else {
        repeatCount = 1;
      }
      lastTarget = actionable;
      lastTime = now;

      // Clasificación del clic.
      const isCta =
        actionable.hasAttribute("data-tw-cta") ||
        /\b(cta|btn|button)\b/i.test(actionable.getAttribute("class") || "") ||
        actionable.tagName.toLowerCase() === "button";

      let outbound = false;
      if (href) {
        try {
          outbound = new URL(href, location.href).host !== location.host;
        } catch {
          /* anchor interno tipo #foo */
        }
      }

      emit("click", { selector, text, href });
      if (isCta) emit("cta_click", { selector, text, href });
      if (outbound) emit("outbound_click", { selector, href });

      ctx.touch();
    },
    { passive: true, capture: true }
  );
}
